import { useState } from 'react';
import {
  Badge,
  Button,
  SegmentedTabs,
  Spinner,
  Tooltip,
} from '@skupreme/shared/ui';
import { ChannelMark } from '@features/catalog/components/v2/ChannelMark';
import { useAccountView } from '@features/account-view/useAccountView';
import {
  providerStatus,
  timeAgo,
} from '@features/storage-providers/lib/provider-status';
import type {
  ConnectionsScope,
  RebuildConnectionKind,
  SettingsConnection,
} from '../rebuild-settings-service';
import { useConnections } from '../use-rebuild-settings';
import {
  ConnectionHistoryDialog,
  healthLabel,
} from './ConnectionHistoryDialog';
import { ListCard } from './RebuildSettingsSections';

const KINDS: Record<RebuildConnectionKind, string> = {
  marketplace: 'Sales channel',
  storage_provider: 'Storage provider',
  other: 'Carrier',
};

const SCOPES = [
  { id: 'division', label: 'This division' },
  { id: 'account', label: 'Whole account' },
];

const nameOf = (connection: SettingsConnection) =>
  connection.name ??
  connection.provider ??
  `Connection ${connection.id.slice(0, 8)}`;

/**
 * The new system's connections (sales channels, storage providers, carrier
 * accounts) with their status: the signed-in division's, or with the account
 * view every division's, each naming the divisions it serves.
 */
export function ConnectionsSection() {
  const { view } = useAccountView();
  const [scope, setScope] = useState<ConnectionsScope>('division');
  const [history, setHistory] = useState<SettingsConnection | null>(null);
  const isAccount = scope === 'account' && view.available;
  const { data, isLoading, isError } = useConnections(
    isAccount ? 'account' : 'division',
  );
  const divisionName = (id: string) =>
    (view.available && view.divisions.find(d => d.id === id)?.name) ||
    `Division ${id.slice(-4)}`;

  if (isLoading) return <Spinner />;
  const connections = data?.v2 ? data.connections : undefined;

  return (
    <>
      <ListCard
        title="Connections"
        action={
          view.available ? (
            <SegmentedTabs
              label="Connections of"
              items={SCOPES}
              selectedKey={isAccount ? 'account' : 'division'}
              onSelectionChange={key => setScope(key as ConnectionsScope)}
            />
          ) : undefined
        }
        columns={[
          'Connection',
          'Type',
          'Status',
          'Last change',
          ...(isAccount ? ['Divisions'] : []),
        ]}
        rows={
          isError
            ? undefined
            : (connections ?? []).map(connection => {
                const status = providerStatus(connection.status ?? 'unknown');
                return {
                  id: connection.id,
                  cells: [
                    <span
                      key="name"
                      className="sk:flex sk:items-center sk:gap-2"
                    >
                      {connection.kind === 'marketplace' ? (
                        <ChannelMark
                          channel={{
                            provider: connection.provider,
                            display_name: connection.name,
                          }}
                        />
                      ) : null}
                      {nameOf(connection)}
                    </span>,
                    KINDS[connection.kind],
                    <span key="status" className="sk:flex sk:gap-1">
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {connection.routingPaused ? (
                        <Badge tone="warning">Routing paused</Badge>
                      ) : null}
                      {(connection.pausedStreams ?? []).map(paused => (
                        <Tooltip
                          key={paused.stream}
                          content={paused.reason ?? ''}
                          isDisabled={!paused.reason}
                        >
                          <Badge tone="warning">Paused: {paused.stream}</Badge>
                        </Tooltip>
                      ))}
                    </span>,
                    <span
                      key="last"
                      className="sk:flex sk:flex-col sk:items-start"
                    >
                      {connection.lastEvent ? (
                        <span className="sk:text-dense sk:text-fg-secondary">
                          {connection.lastEvent.stream
                            ? `${connection.lastEvent.stream}: `
                            : ''}
                          {healthLabel(connection.lastEvent.to)},{' '}
                          {timeAgo(connection.lastEvent.at)}
                        </span>
                      ) : (
                        <span className="sk:text-dense sk:text-fg-tertiary">
                          —
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() => setHistory(connection)}
                      >
                        History
                      </Button>
                    </span>,
                    ...(isAccount
                      ? [
                          connection.divisionIds.length
                            ? connection.divisionIds
                                .map(divisionName)
                                .join(', ')
                            : 'Account',
                        ]
                      : []),
                  ],
                };
              })
        }
        emptyMessage="No connections on the new system yet."
      />
      <ConnectionHistoryDialog
        connectionId={history?.id ?? null}
        name={history ? nameOf(history) : ''}
        onClose={() => setHistory(null)}
      />
    </>
  );
}
